import {
  convertDateToYYYYMMDDFormat,
  convertEventSelectToTarget,
  CustomDateRangePicker,
  formatDataToLabelValue,
  NoDataComponent,
  PageWrapper,
  Select,
  SimpleTableLoadingSkeleton,
  STATUSPAGE,
  TableVirtualized,
} from '@repo/ui';
import { useFormik } from 'formik';
import noData from 'presentation/app/assets/images/no-task.png';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import { useEffect, useState } from 'react';
import { Col, Card, Label, Spinner, Row } from 'reactstrap';
import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';

import { makeMissedActivitiesJustificationsReportColumns } from '../../../common/columns/activities/missed-activities-justifications';
import { useMissedActivitiesJustifications } from '../../../hooks/useMissedActivitiesJustifications';

export const MissedActivitiesJustifications = () => {
  const [buttonSelectedSubmit, setButtonSelectedSubmit] = useState<string>();

  const {
    statusLoading,
    hasFetchedData,
    cancellationReasons,
    missedActivitiesJustifications,
    requestGetAllCancellationReasons,
    handleMissedActivitiesJustificationsFile,
    requestMissedActivitiesJustificationsJSON,
  } = useMissedActivitiesJustifications();

  function getCurrentDate() {
    return new Date();
  }

  function getFirstDayOfMonth() {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      entity: null,
      course: null,
      unit: null,
      semester: null,
      groups: [],
      starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
      ends: convertDateToYYYYMMDDFormat(getCurrentDate()),
      reasonsForJustification: [],
    },
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string().nullable(),
          value: Yup.string().nullable(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      starts: Yup.date().required('Data inicial é obrigatória.'),
      ends: Yup.date().required('Data final é obrigatória.'),
      reasonsForJustification: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
    }),

    onSubmit: (values) => {
      const groupHashIds = values.groups.map((group) => group.value);
      const cancellationReasonIds = values.reasonsForJustification.map(
        (reason) => reason.value,
      );

      const valuesToSubmit = {
        groupHashIds,
        organizationHashId: validation?.values?.entity?.value,
        courseHashId: validation?.values?.course?.value,
        semesterId: validation?.values?.semester?.value,
        curricularUnitHashId: validation?.values?.unit?.value,
        cancellationReasonIds,
        startsAt: values.starts,
        endsAt: values.ends,
      };

      {
        buttonSelectedSubmit === 'json' &&
          requestMissedActivitiesJustificationsJSON({
            ...valuesToSubmit,
            type: '2',
          });
      }
      {
        buttonSelectedSubmit === 'pdf' &&
          handleMissedActivitiesJustificationsFile({
            ...valuesToSubmit,
            type: '0',
          });
      }
    },
  });

  useEffect(() => {
    requestGetAllCancellationReasons();
  }, []);

  const formattedData = missedActivitiesJustifications?.map((register) => {
    return {
      id: uuid(),
      ...register,
      ...(register.justification && {
        hasFooter: true,
        footerText: `Justificativa: ${register.justification}`,
      }),
    };
  });

  return (
    <PageWrapper title="Perdidas Justificadas">
      <Col className="mb-3 border-0 d-flex flex-column">
        <h4>Perdidas Justificadas</h4>
        <p className="text-muted mb-0">
          Visualize e exporte relatórios detalhados das atividades perdidas.
        </p>
      </Col>

      <Card>
        <ReportsGeneralFilters
          validation={validation}
          inputsChildren={
            <>
              <Col sm={12} md={6} lg={3} className="mb-3">
                <Row>
                  <Label htmlFor="initial-date" className="form-label">
                    Período
                  </Label>
                  <CustomDateRangePicker
                    placement="auto"
                    onDateChange={(start, ends) => {
                      validation.setFieldValue(
                        'starts',
                        convertDateToYYYYMMDDFormat(start),
                      );
                      validation.setFieldValue(
                        'ends',
                        convertDateToYYYYMMDDFormat(ends),
                      );
                    }}
                  />
                </Row>
              </Col>

              <Col sm={12} md={4} lg={4} className="mb-3">
                <Label htmlFor="reasonsForJustification" className="form-label">
                  Motivo da Justificativa
                </Label>
                <Select
                  isClearable
                  isMulti
                  id="reasonsForJustification"
                  name="reasonsForJustification"
                  placeholder="Selecione os motivos"
                  value={validation.values.reasonsForJustification}
                  onChange={(e) => {
                    validation.handleChange(
                      convertEventSelectToTarget('reasonsForJustification', e),
                    );
                  }}
                  onBlur={validation.handleBlur}
                  options={formatDataToLabelValue(
                    cancellationReasons as never,
                    'id',
                    'name',
                  )}
                />
              </Col>
            </>
          }
          buttonsChildren={
            <div className="d-flex flex-wrap gap-2 justify-content-end align-content-end">
              <button
                id="json"
                type="submit"
                onClick={() => {
                  setButtonSelectedSubmit('json');
                }}
                className="btn btn-primary waves-effect waves-light"
                disabled={Boolean(statusLoading)}
              >
                {buttonSelectedSubmit === 'json' && Boolean(statusLoading) ? (
                  <Spinner size="sm">Carregando...</Spinner>
                ) : (
                  <>
                    Visualizar
                    <i className="ri-computer-line mx-2"></i>
                  </>
                )}
              </button>

              <button
                type="submit"
                id="pdf"
                onClick={() => {
                  setButtonSelectedSubmit('pdf');
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={Boolean(statusLoading)}
              >
                {buttonSelectedSubmit === 'pdf' && Boolean(statusLoading) ? (
                  <Spinner size="sm">Carregando...</Spinner>
                ) : (
                  <>
                    Visualizar em PDF
                    <i className="las la-file-pdf mx-2"></i>
                  </>
                )}
              </button>
            </div>
          }
        />

        {missedActivitiesJustifications?.length === 0 &&
          hasFetchedData &&
          statusLoading === STATUSPAGE.DONE && (
            <div className="p-4">
              <NoDataComponent src={noData} />
            </div>
          )}

        {statusLoading === STATUSPAGE.LOADING && <SimpleTableLoadingSkeleton />}

        {statusLoading === STATUSPAGE.DONE &&
          missedActivitiesJustifications?.length > 0 && (
            <TableVirtualized
              columns={makeMissedActivitiesJustificationsReportColumns()}
              data={formattedData}
            />
          )}
      </Card>
    </PageWrapper>
  );
};
